import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

/* ─────────── Cabeçalho de página ─────────── */

export function PageHeader({
  titulo,
  subtitulo,
  acoes,
  voltar,
}: {
  titulo: string;
  subtitulo?: ReactNode;
  acoes?: ReactNode;
  voltar?: { para: string; texto: string };
}) {
  return (
    <div className="page-header">
      <div>
        <div className="page-title">{titulo}</div>
        {subtitulo && <div className="page-subtitle">{subtitulo}</div>}
      </div>
      {/*
        ⚠️ **O voltar sai do `acoes` e não depende dele.**

        `acoes` costuma vir embrulhado num `pode(...)` — na tela de
        recomendações, inteiro dentro de `recommendations.send`. Se o voltar
        fosse só mais um filho de `acoes`, quem não tem a permissão de enviar
        ficaria sem saída: a tela abriria para leitura e sem caminho de volta.
      */}
      {(voltar || acoes) && (
        <div className="header-actions">
          {voltar && (
            <Link to={voltar.para} className="btn btn-secondary">
              <i className="ti ti-arrow-left" /> {voltar.texto}
            </Link>
          )}
          {acoes}
        </div>
      )}
    </div>
  );
}

/* ─────────── Estados de carga, erro e vazio ─────────── */

export function Carregando({ texto = 'Carregando…' }: { texto?: string }) {
  return (
    <div className="estado-centro">
      <i className="ti ti-loader-2 girando" />
      <span>{texto}</span>
    </div>
  );
}

export function ErroBox({ erro, aoTentar }: { erro: unknown; aoTentar?: () => void }) {
  const mensagem = erro instanceof Error ? erro.message : 'Erro inesperado.';

  return (
    <div className="card erro-box">
      <i className="ti ti-alert-triangle" />
      <div style={{ flex: 1 }}>
        <strong>Não deu certo</strong>
        <div style={{ color: 'var(--text-muted)', marginTop: 2 }}>{mensagem}</div>
      </div>
      {aoTentar && (
        <button className="btn btn-secondary btn-sm" onClick={aoTentar}>
          Tentar de novo
        </button>
      )}
    </div>
  );
}

export function Vazio({
  icone = 'ti-inbox',
  titulo,
  descricao,
  acao,
}: {
  icone?: string;
  titulo: string;
  descricao?: string;
  acao?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <i className={`ti ${icone}`} style={{ fontSize: 32, color: 'var(--text-dim)' }} />
      <div style={{ fontWeight: 600, marginTop: 12 }}>{titulo}</div>
      {descricao && (
        <div style={{ color: 'var(--text-muted)', marginTop: 4, maxWidth: 420 }}>{descricao}</div>
      )}
      {acao && <div style={{ marginTop: 16 }}>{acao}</div>}
    </div>
  );
}

/* ─────────── Modal ─────────── */

/**
 * Quem está por cima. Com dois modais abertos — configurar cliente e, sobre
 * ele, definir senha — um Esc fechava os dois de uma vez: ambos ouviam a mesma
 * tecla no `document`. Agora só o último a abrir responde.
 */
const modaisAbertos: symbol[] = [];

export function Modal({
  titulo,
  descricao,
  aoFechar,
  children,
  largura = 520,
}: {
  titulo: string;
  descricao?: string;
  aoFechar: () => void;
  children: ReactNode;
  largura?: number;
}) {
  // Numa ref, e não na lista de dependências: `aoFechar` costuma ser uma função
  // criada a cada render, e reexecutar o efeito reordenaria a pilha — o modal
  // de baixo voltaria para o topo só por ter renderizado de novo.
  const fechar = useRef(aoFechar);
  fechar.current = aoFechar;

  // Esc fecha: um formulário aberto por engano não deve exigir mira no X.
  useEffect(() => {
    const meuLugar = Symbol('modal');
    modaisAbertos.push(meuLugar);

    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (modaisAbertos[modaisAbertos.length - 1] === meuLugar) fechar.current();
    };

    document.addEventListener('keydown', aoTeclar);
    return () => {
      modaisAbertos.splice(modaisAbertos.indexOf(meuLugar), 1);
      document.removeEventListener('keydown', aoTeclar);
    };
  }, []);

  return (
    <div className="modal-fundo" onMouseDown={aoFechar}>
      <div
        className="modal"
        style={{ maxWidth: largura }}
        // Sem isto, arrastar o texto de dentro para fora fecharia o modal.
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
      >
        <div className="modal-topo">
          <div>
            <div className="modal-titulo">{titulo}</div>
            {descricao && <div className="modal-descricao">{descricao}</div>}
          </div>
          <button className="icon-btn" onClick={aoFechar} aria-label="Fechar">
            <i className="ti ti-x" style={{ fontSize: 16 }} />
          </button>
        </div>
        {/*
          A rolagem fica AQUI, e não no fundo da tela: com ela lá fora, o
          cabeçalho, as abas e os botões subiam junto com o formulário — e num
          modal alto o "Salvar" ficava fora de alcance.
        */}
        <div className="modal-corpo">{children}</div>
      </div>
    </div>
  );
}

export function ModalRodape({ children }: { children: ReactNode }) {
  return <div className="modal-rodape">{children}</div>;
}

/* ─────────── Formulário ─────────── */

export function Form({
  aoEnviar,
  children,
}: {
  aoEnviar: (dados: Record<string, string>) => void;
  children: ReactNode;
}) {
  function submeter(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const dados: Record<string, string> = {};
    for (const [chave, valor] of form.entries()) {
      if (typeof valor === 'string') dados[chave] = valor.trim();
    }
    aoEnviar(dados);
  }

  return <form onSubmit={submeter}>{children}</form>;
}

export function Campo({
  label,
  name,
  tipo = 'text',
  obrigatorio,
  opcional,
  explicacao,
  valorInicial,
  placeholder,
  ajuda,
  erro,
  opcoes,
  autoFocus,
  linhas,
  valor,
  aoMudar,
}: {
  label: string;
  name: string;
  tipo?: string;
  obrigatorio?: boolean;
  /** Diz em voz alta que dá para deixar em branco — para quando a dúvida é provável. */
  opcional?: boolean;
  /** O "?" ao lado do rótulo: o que o campo faz, para quem não sabe. Ver `Dica`. */
  explicacao?: string;
  valorInicial?: string;
  placeholder?: string;
  ajuda?: string;
  erro?: string;
  /** Presente = <select>. */
  opcoes?: { valor: string; rotulo: string }[];
  autoFocus?: boolean;
  /** Presente = <textarea> com esta altura em linhas. */
  linhas?: number;
  /**
   * Controlado. Só para quando algo FORA do campo depende do que se digita —
   * a pré-visualização do template, por exemplo. Sem isto, o padrão do
   * formulário continua sendo não-controlado, que é mais simples e mais rápido.
   */
  valor?: string;
  aoMudar?: (v: string) => void;
}) {
  const controle = valor !== undefined ? { value: valor } : { defaultValue: valorInicial };

  return (
    <label className="campo">
      <span className="campo-label">
        {label}
        {obrigatorio && <span style={{ color: 'var(--danger)' }}> *</span>}
        {opcional && <span className="campo-opcional"> (opcional)</span>}
        {explicacao && <Dica texto={explicacao} />}
      </span>

      {linhas ? (
        <textarea
          className="form-input"
          name={name}
          rows={linhas}
          placeholder={placeholder}
          required={obrigatorio}
          autoFocus={autoFocus}
          style={{ resize: 'vertical', lineHeight: 1.5 }}
          onChange={(e) => aoMudar?.(e.target.value)}
          {...controle}
        />
      ) : opcoes ? (
        <select
          name={name}
          className="form-select"
          required={obrigatorio}
          onChange={(e) => aoMudar?.(e.target.value)}
          {...controle}
        >
          {opcoes.map((o) => (
            <option key={o.valor} value={o.valor}>
              {o.rotulo}
            </option>
          ))}
        </select>
      ) : (
        <input
          className="form-input"
          name={name}
          type={tipo}
          placeholder={placeholder}
          required={obrigatorio}
          autoFocus={autoFocus}
          onChange={(e) => aoMudar?.(e.target.value)}
          {...controle}
        />
      )}

      {erro ? (
        <span className="campo-erro">{erro}</span>
      ) : (
        ajuda && <span className="campo-ajuda">{ajuda}</span>
      )}
    </label>
  );
}

/**
 * Um "?" que abre uma explicação.
 *
 * Clique, e não só passar o mouse: `title` e `:hover` não existem no celular, e
 * explicação que só aparece no computador é explicação que metade das pessoas
 * não vê. No computador o mouse também abre, por conveniência.
 *
 * É um `<button type="button">` dentro do `<label>` do campo: o tipo impede que
 * ele envie o formulário, e o `preventDefault` impede que o clique vá parar no
 * campo em vez de abrir a explicação.
 */
export function Dica({ texto }: { texto: string }) {
  const [aberta, setAberta] = useState(false);

  return (
    <span className={`dica${aberta ? ' aberta' : ''}`}>
      <button
        type="button"
        className="dica-botao"
        aria-label="O que é isto?"
        aria-expanded={aberta}
        onClick={(e) => {
          e.preventDefault();
          setAberta((v) => !v);
        }}
        onBlur={() => setAberta(false)}
      >
        <i className="ti ti-help-circle" />
      </button>
      <span className="dica-texto" role="tooltip">
        {texto}
      </span>
    </span>
  );
}

export function LinhaCampos({ children }: { children: ReactNode }) {
  return <div className="campos-linha">{children}</div>;
}

/* ─────────── Paginação ─────────── */

export function Paginacao({
  meta,
  aoMudar,
}: {
  meta: { page: number; perPage: number; total: number; totalPages: number };
  aoMudar: (pagina: number) => void;
}) {
  if (meta.total === 0) return null;

  const de = (meta.page - 1) * meta.perPage + 1;
  const ate = Math.min(meta.page * meta.perPage, meta.total);

  return (
    <div className="paginacao">
      <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>
        {de}–{ate} de {meta.total}
      </span>
      <div style={{ display: 'flex', gap: 6 }}>
        <button
          className="btn btn-secondary btn-sm"
          disabled={meta.page <= 1}
          onClick={() => aoMudar(meta.page - 1)}
        >
          <i className="ti ti-chevron-left" /> Anterior
        </button>
        <button
          className="btn btn-secondary btn-sm"
          disabled={meta.page >= meta.totalPages}
          onClick={() => aoMudar(meta.page + 1)}
        >
          Próxima <i className="ti ti-chevron-right" />
        </button>
      </div>
    </div>
  );
}

/* ─────────── Busca ─────────── */

export function CampoBusca({
  valor,
  aoMudar,
  placeholder = 'Buscar…',
}: {
  valor: string;
  aoMudar: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="busca">
      <i className="ti ti-search" style={{ fontSize: 14, color: 'var(--text-dim)' }} />
      <input
        className="busca-input"
        value={valor}
        placeholder={placeholder}
        onChange={(e) => aoMudar(e.target.value)}
      />
      {valor && (
        <button className="icon-btn" onClick={() => aoMudar('')} aria-label="Limpar busca">
          <i className="ti ti-x" style={{ fontSize: 13 }} />
        </button>
      )}
    </div>
  );
}

/* ─────────── Seção dentro de uma página ─────────── */

/**
 * Título, explicação e uma ação, acima de um bloco de conteúdo.
 *
 * Vive aqui, e não na tela do evento onde nasceu, porque as abas de conteúdo e
 * a lista de participantes passaram a dividi-la — e cada uma importar da outra
 * fecharia um ciclo entre os dois módulos.
 *
 * A descrição não é enfeite: é onde cabe dizer para que serve aquele bloco sem
 * gastar uma linha de ajuda em cima de cada campo.
 */
export function Secao({
  titulo,
  descricao,
  acao,
  children,
}: {
  titulo: string;
  descricao?: string;
  acao?: ReactNode;
  children: ReactNode;
}) {
  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 16,
          marginBottom: 16,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{titulo}</div>
          {descricao && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3, maxWidth: 620 }}>
              {descricao}
            </div>
          )}
        </div>
        {acao}
      </div>
      {children}
    </>
  );
}
